import { Injectable } from "@angular/core";
import { BaseLookupService } from "../../impl/BaseLookupService";
import { IOrderDataLookupService } from "../IOrderDataLookupService";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
import { SiblingRelationshipManager } from "./SiblingRelationshipManager";

const logger = LoggerService.for(__dirname);

import { FlashingWaitTimeManager } from "./FlashingWaitTimeManager";
import { BfxConfigIDRefHelper } from "../../../../helper-services/bfx-config-id-ref-helper";

@Injectable()
export class OrderDataLookupService
  extends BaseLookupService<string, CubiqTS.OrderDataImpl>
  implements IOrderDataLookupService
{
  private assetCodeOrderIdMultimap: Map<string, Set<string>> = new Map<string, Set<string>>();
  public flashManager: FlashingWaitTimeManager;

  private siblingRelationshipManager: SiblingRelationshipManager;

  constructor() {
    super(CubiqTS.OrderDataImpl);

    this.flashManager = new FlashingWaitTimeManager();
    this.siblingRelationshipManager = new SiblingRelationshipManager();
  }

  reset() {
    super.reset();

    this.assetCodeOrderIdMultimap.clear();
    this.siblingRelationshipManager.clear();
  }

  deleteItem(item: CubiqTS.OrderDataImpl): void {
    super.deleteItem(item);

    if (item.gammaRungId) {
      // Unregister any gamma strategy relationship
      this.siblingRelationshipManager.removeOrder(item);
    }
    this.flashManager.removeWaitHandler(item);
    this.removePairMap(item, this.getItemKey(item));
  }

  private addPairMap(item: CubiqTS.OrderDataImpl, id: string) {
    const assetCode = `${item.baseCode}${item.nonBaseCode}`;

    let orderIds: Set<string> = this.assetCodeOrderIdMultimap.get(assetCode);

    if (orderIds instanceof Set) orderIds.add(id);
    else orderIds = new Set([id]);

    this.assetCodeOrderIdMultimap.set(assetCode, orderIds);
  }

  private removePairMap(item: CubiqTS.OrderDataImpl, id: string) {
    const assetCode = `${item.baseCode}${item.nonBaseCode}`;
    let orderIds: Set<string> = this.assetCodeOrderIdMultimap.get(assetCode);
    let allDeleted = false;

    if (orderIds instanceof Set) {
      orderIds.delete(id);

      allDeleted = orderIds.size === 0;
    }

    if (allDeleted) this.assetCodeOrderIdMultimap.delete(assetCode);
    else this.assetCodeOrderIdMultimap.set(assetCode, orderIds);
  }

  mapItem(item: CubiqTS.OrderDataImpl): void {
    item = this.preprocessOrderData(item);
    super.mapItem(item);
    this.flashManager.checkOrderForFlashHandle(item);
    this.addPairMap(item, this.getItemKey(item));
  }

  mapItemById(item: CubiqTS.OrderDataImpl, id: string) {
    item = this.preprocessOrderData(item);
    super.mapItemById(item, id);
    this.flashManager.checkOrderForFlashHandle(item);
    this.addPairMap(item, id);
  }

  public preprocessOrderData(orderData: CubiqTS.OrderDataImpl): CubiqTS.OrderDataImpl {
    if (orderData) {
      orderData["primaryKey"] = orderData.orderId;

      // Currently we do not create a sibling relationship for OCOs as these already exist
      if (orderData.gammaRungId) {
        // Setup gamma strategy relationship
        orderData.siblingRelationship = this.siblingRelationshipManager.getRelationship(orderData);
        orderData.strategyType = CubiqTS.StrategyType.GAMMA;
      }
    }
    return orderData;
  }

  getItemKey(item: CubiqTS.OrderDataImpl): string {
    let order = item as CubiqTS.OrderDataImpl;
    return order.orderId.toString();
  }

  getOrderData(id: string): CubiqTS.OrderDataImpl {
    let orderData = this.getItem(id) as CubiqTS.OrderDataImpl;
    if (orderData) {
      orderData = Object.assign(new CubiqTS.OrderDataImpl(), orderData);
    }
    return orderData;
  }

  getOrderDataForIds(ids: Array<string>): Array<CubiqTS.OrderDataImpl> {
    let orders: Array<CubiqTS.OrderDataImpl> = [];
    for (let id of ids) {
      let orderData = this.getItem(id) as CubiqTS.OrderDataImpl;
      if (orderData) {
        orders.push(Object.assign(new CubiqTS.OrderDataImpl(), orderData));
      }
    }
    return orders;
  }

  getOrderRef(id: string): string | null {
    let orderData = this.getOrderData(id);
    let orderRef = null;
    if (orderData) {
      orderRef = BfxConfigIDRefHelper.getOrderRef(orderData);
    }
    return orderRef;
  }

  getOrderRefForIds(ids: string[]): Array<string | null> {
    if (Array.isArray(ids)) {
      return ids.map((id) => this.getOrderRef(id));
    }
    return null;
  }

  hasActivePair(assetCode: string): boolean {
    return this.assetCodeOrderIdMultimap.has(assetCode);
  }

  getActivePairs(): Set<string> {
    return new Set<string>(this.assetCodeOrderIdMultimap.keys());
  }

  getActiveOrderIdsByAssetCode(assetCode: string) {
    return new Set<string>(this.assetCodeOrderIdMultimap.get(assetCode));
  }

  // public getCachedAdaptItem(orderData): any|null {
  //   if (!this.modelObjectCache.has(orderData))
  //     return null;

  //   return this.modelObjectCache.get(orderData);
  // }

  // public updateCachedAdaptItem(orderData, updated): void {
  //   // update adapted cache for rate in-place
  //   let cachedRowData = this.getCachedAdaptItem(orderData);

  //   if (cachedRowData != null && updated != null)
  //     Object.assign(cachedRowData, updated);
  // }
}
